import { Component } from '@theme/component';
import {
  ThemeEvents,
  BundleProductToggleEvent,
  CartAddEvent,
} from '@theme/events';
import { formatMoney } from '@theme/utilities';
import { Swiper } from '@theme/swiper';

class ProductBundleCardComponent extends Component {
  requiredRefs = ['card'];

  connectedCallback() {
  super.connectedCallback();

  // ✅ Dispatch initial state so parent bundle counts this card on load
  const { card } = this.refs;
  if (card?.hasAttribute('data-selected')) {
    this.dispatchEvent(
      new BundleProductToggleEvent(
        true,
        card.dataset.price || null,
        card.dataset.comparePrice || null,
        card.dataset.variantId || null,
      ),
    );
  }
}


  /**
   * @param {MouseEvent & {target: HTMLElement}} event - Click event.
   */
  toggleCheckbox(event) {
    const { card } = this.refs;
    card.toggleAttribute('data-selected');

    this.dispatchEvent(
      new BundleProductToggleEvent(
        card.hasAttribute('data-selected'),
        card.dataset.price || null,
        card.dataset.comparePrice || null,
        card.dataset.variantId || null,
      ),
    );
  }
}

if (!customElements.get('product-bundle-card-component')) {
  customElements.define(
    'product-bundle-card-component',
    ProductBundleCardComponent,
  );
}

class ProductBundleComponent extends Component {
  requiredRefs = [
    'bundleCount',
    'btnBundleCount',
    'comparePrice',
    'price',
    'btnAddBundleToCart',
  ];

  /**
   * The observer for the product bundle recommendations
   * @type {IntersectionObserver}
   */
  #intersectionObserver = new IntersectionObserver(
    (entries, observer) => {
      if (!entries[0]?.isIntersecting) return;

      observer.disconnect();
      this.#loadRecommendations();
    },
    { rootMargin: '0px 0px 400px 0px' },
  );

  /**
   * Observing changes to the elements attributes
   * @type {MutationObserver}
   */
  #mutationObserver = new MutationObserver((mutations) => {
    for (const mutation of mutations) {
      if (mutation.target !== this || mutation.type !== 'attributes') continue;

      if (
        mutation.attributeName === 'data-recommendations-performed' &&
        this.dataset.recommendationsPerformed === 'true'
      )
        continue;

      if (mutation.attributeName === 'data-algolia-loaded') continue;

      this.#loadRecommendations();
      break;
    }
  });

  /**
   * The cached recommendations
   * @type {Record<string, string>}
   */
  #cachedRecommendations = {};

  /**
   * An abort controller for the active fetch (if there is one)
   * @type {AbortController | null}
   */
  #activeFetch = null;

  connectedCallback() {
    super.connectedCallback();

    // Check if this is a recommendations-based bundle
    if (this.dataset.url || this.dataset.algoliaRelated) {
      // Recommendation type: using Algolia's recommendations
      if (this.dataset.algoliaRelated) {
        this.#intersectionObserver.observe(this);
        this.#mutationObserver.observe(this, { attributes: true });
      } else {
        // Recommendation type: related or complementary using Shopify's API
        this.#intersectionObserver.observe(this);
        this.#mutationObserver.observe(this, { attributes: true });
      }
    } else {
      // Metafield-based bundle, initialize carousel immediately
      this.#initCarousel();
    }

    this.addEventListener(
      ThemeEvents.bundleProductToggle,
      this.onBundleProductToggle,
    );

    this.variantIds = this.refs.btnAddBundleToCart.dataset.variantIds
      .split('||')
      .filter((/** @type {string} */ id) => id);
  }

  disconnectedCallback() {
    this.#intersectionObserver.disconnect();
    this.#mutationObserver.disconnect();
    this.#activeFetch?.abort();
  }

  /**
   * Load the product recommendations
   */
  #loadRecommendations() {
    const { productId, algoliaRelated } = this.dataset;
    const id = this.id;

    if (!productId || !id) {
      throw new Error('Product ID and an ID attribute are required');
    }

    if (algoliaRelated === 'true') {
      if (this.dataset.algoliaLoaded === 'true') {
        return;
      }
      this.#loadAlgoliaRecommendations();
      return;
    }

    const { recommendationsPerformed, sectionId, intent } = this.dataset;

    // If recommendations are already performed server-side, just initialize components
    if (recommendationsPerformed === 'true') {
      this.#initCarousel();
      return;
    }

    this.#fetchCachedRecommendations(productId, sectionId, intent)
      // @ts-ignore - Fire and forget promise pattern
      .then((result) => {
        if (!result.success) {
          if (!Shopify.designMode) {
            this.#handleError(new Error(`Server returned ${result.status}`));
          }
          return;
        }

        const html = document.createElement('div');
        html.innerHTML = result.data || '';
        const recommendations = html.querySelector(
          `product-bundle-component[id="${id}"]`,
        );

        if (
          recommendations?.innerHTML &&
          recommendations.innerHTML.trim().length
        ) {
          this.dataset.recommendationsPerformed = 'true';
          this.innerHTML = recommendations.innerHTML;

          this.#initCarousel();

          // Manually query and update required refs immediately after innerHTML replacement
          // This ensures refs point to the new DOM nodes, not the old detached ones
          this.refs.bundleCount = this.querySelector('[ref="bundleCount"]');
          this.refs.btnBundleCount = this.querySelector(
            '[ref="btnBundleCount"]',
          );
          this.refs.comparePrice = this.querySelector('[ref="comparePrice"]');
          this.refs.price = this.querySelector('[ref="price"]');
          this.refs.btnAddBundleToCart = this.querySelector(
            '[ref="btnAddBundleToCart"]',
          );

          // Re-setup event listeners after innerHTML change
          this.addEventListener(
            ThemeEvents.bundleProductToggle,
            this.onBundleProductToggle,
          );

          // Re-initialize variant IDs from the updated button
          if (this.refs.btnAddBundleToCart) {
            this.variantIds =
              this.refs.btnAddBundleToCart.dataset.variantIds
                ?.split('||')
                .filter((/** @type {string} */ id) => id) || [];
          }
        } else {
          this.#handleError(new Error('No recommendations available'));
        }
      })
      .catch((e) => {
        this.#handleError(e);
      });
  }

  /**
   * Load Algolia recommendations using the Recommendations API
   */
  async #loadAlgoliaRecommendations() {
    const {
      algoliaAppId,
      algoliaApiKey,
      algoliaIndex,
      variantId,
      productsLimit,
    } = this.dataset;

    if (!algoliaAppId || !algoliaApiKey || !algoliaIndex) {
      this.#handleError(new Error('Missing required Algolia configuration'));
      return;
    }

    if (!variantId) {
      this.#handleError(
        new Error('Missing variant ID for Algolia recommendations'),
      );
      return;
    }

    const objectID = variantId;

    this.#activeFetch?.abort();
    this.#activeFetch = new AbortController();

    try {
      const requestBody = {
        requests: [
          {
            indexName: algoliaIndex,
            model: 'related-products',
            objectID: objectID,
            threshold: 0,
            maxRecommendations: parseInt(productsLimit || '5') || 5,
          },
        ],
      };

      const response = await fetch(
        `https://${algoliaAppId}.algolia.net/1/indexes/*/recommendations`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'X-Algolia-Application-Id': algoliaAppId,
            'X-Algolia-API-Key': algoliaApiKey,
          },
          body: JSON.stringify(requestBody),
          signal: this.#activeFetch.signal,
        },
      );

      if (!response.ok) {
        const errorText = await response.text();
        console.error('Algolia API Error Response:', errorText);
        throw new Error(
          `Algolia API returned ${response.status}: ${errorText}`,
        );
      }

      const data = await response.json();
      const recommendations = data.results?.[0]?.hits || [];

      this.classList.remove('is-hidden');
      if (recommendations.length === 0) {
        console.warn(
          'No Algolia recommendations available for this product. This could mean:',
          '\n1. The Related Products model needs to be trained in Algolia',
          '\n2. There are no related products for this item',
        );
        this.classList.add('is-hidden');
        this.dataset.algoliaLoaded = 'true';
        return;
      }

      this.#renderAlgoliaRecommendations(recommendations);
      this.dataset.algoliaLoaded = 'true';
    } catch (error) {
      if (error.name === 'AbortError') return;
      this.#handleError(error);
    } finally {
      this.#activeFetch = null;
    }
  }

  /**
   * Render Algolia recommendations as product bundle cards
   * @param {any[]} recommendations
   */
  #renderAlgoliaRecommendations(recommendations) {
    const productsContainer = this.querySelector('.swiper-wrapper');
    if (!productsContainer) return;

    productsContainer.innerHTML = '';

    let bundlePrice = 0;
    let bundleComparePrice = 0;
    const variantIds = [];

    for (const hit of recommendations) {
      const productCard = this.#createBundleCard(hit);
      productsContainer.appendChild(productCard);

      const price = Math.round(hit.price * 100);
      const comparePrice = Math.round(
        (hit.compare_at_price || hit.price) * 100,
      );
      bundlePrice += price;
      bundleComparePrice += comparePrice;
      variantIds.push(hit.objectID);
    }

    const bundleCount = recommendations.length;
    const bundleCountEl = this.querySelector('.product-bundle__count');
    if (bundleCountEl) {
      bundleCountEl.innerHTML =
        Theme.translations.bundle_count?.replace(
          '{{ count }}',
          `<span ref="bundleCount">${bundleCount}</span>`,
        ) || String(bundleCount);
    }

    if (this.refs.comparePrice) {
      this.refs.comparePrice.dataset.comparePrice =
        bundleComparePrice.toString();
      this.refs.comparePrice.textContent = formatMoney(bundleComparePrice);
      this.refs.comparePrice.hidden = bundleComparePrice === bundlePrice;
    }

    if (this.refs.price) {
      this.refs.price.dataset.price = bundlePrice.toString();
      this.refs.price.textContent = formatMoney(bundlePrice);
    }

    if (this.refs.btnAddBundleToCart) {
      this.refs.btnAddBundleToCart.dataset.variantIds = variantIds.join('||');
      const btnText = this.refs.btnAddBundleToCart.querySelector(
        '.product-bundle__add-button-text',
      );
      if (btnText) {
        btnText.innerHTML =
          Theme.translations.bundle_add_to_cart?.replace(
            '{{ count }}',
            `<span ref="btnBundleCount">${bundleCount}</span>`,
          ) || `Add ${bundleCount} to cart`;
      }
    }

    this.variantIds = variantIds;

    this.#initCarousel();

    // Manually query and update required refs immediately after innerHTML replacement
    // This ensures refs point to the new DOM nodes, not the old detached ones
    this.refs.bundleCount = this.querySelector('[ref="bundleCount"]');
    this.refs.btnBundleCount = this.querySelector('[ref="btnBundleCount"]');
    this.refs.comparePrice = this.querySelector('[ref="comparePrice"]');
    this.refs.price = this.querySelector('[ref="price"]');
    this.refs.btnAddBundleToCart = this.querySelector(
      '[ref="btnAddBundleToCart"]',
    );

    // Re-setup event listeners after innerHTML change
    this.addEventListener(
      ThemeEvents.bundleProductToggle,
      this.onBundleProductToggle,
    );

    // Re-initialize variant IDs from the updated button
    if (this.refs.btnAddBundleToCart) {
      this.variantIds =
        this.refs.btnAddBundleToCart.dataset.variantIds
          ?.split('||')
          .filter((/** @type {string} */ id) => id) || [];
    }
  }

  /**
   * Create a bundle card element from Algolia hit
   * @param {any} hit
   * @returns {HTMLElement}
   */
  #createBundleCard(hit) {
    const slide = document.createElement('product-bundle-card-component');
    slide.className = 'product-bundle-card product-bundle__slide swiper-slide';

    const price = Math.round(hit.price * 100);
    const comparePrice = Math.round((hit.compare_at_price || hit.price) * 100);
    const hasDiscount = comparePrice > price;
    const imageUrl = hit.image || hit.images?.[0] || '';

    slide.innerHTML = `
      <div
        class="product-bundle-card__inner"
        ref="card"
        on:click="/toggleCheckbox"
        data-selected
        data-price="${price}"
        data-compare-price="${comparePrice}"
        data-variant-id="${hit.objectID}"
      >
        <button class="product-bundle-card__checkbox button-unstyled">
          <svg viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M12 5.25L6.5 10.25L4 7.97727" stroke="#fff" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"/></svg>
        </button>

        <div class="product-bundle-card__image image">
          <img
            src="${imageUrl}"
            alt="${hit.title || ''}"
            loading="lazy"
          />
        </div>

        <span class="product-bundle-card__title p is-small is-bold">
          ${hit.title || ''}
        </span>

        <div class="product-pricing product-bundle-card__pricing p is-small">
          ${
            hasDiscount
              ? `<p class="product-pricing__compare" data-compare-price="${comparePrice}">
              ${formatMoney(comparePrice)}
            </p>`
              : ''
          }
          <p class="product-pricing__price" data-price="${price}">
            ${formatMoney(price)}
          </p>
        </div>
      </div>
    `;

    return slide;
  }

  /**
   * Fetches the recommendations and cached the result for future use
   * @param {string} productId
   * @param {string | undefined} sectionId
   * @param {string | undefined} intent
   * @returns {Promise<{ success: true, data: string } | { success: false, status: number }>}
   */
  async #fetchCachedRecommendations(productId, sectionId, intent) {
    const url = `${this.dataset.url}&product_id=${productId}&section_id=${sectionId}&intent=${intent}`;

    const cachedResponse = this.#cachedRecommendations[url];
    if (cachedResponse) {
      return { success: true, data: cachedResponse };
    }

    this.#activeFetch?.abort();
    this.#activeFetch = new AbortController();

    try {
      const response = await fetch(url, { signal: this.#activeFetch.signal });
      if (!response.ok) {
        return { success: false, status: response.status };
      }

      const text = await response.text();
      this.#cachedRecommendations[url] = text;
      return { success: true, data: text };
    } finally {
      this.#activeFetch = null;
    }
  }

  /**
   * Handle errors in a consistent way
   * @param {Error} error
   */
  #handleError(error) {
    console.error('Product bundle recommendations error:', error);
    this.classList.add('is-hidden');
    this.dataset.error = 'Error loading product bundle recommendations';
  }

  /**
   * Initialize the carousel
   */
  #initCarousel() {
    const carousel = this.querySelector('.product-bundle__products.swiper');
    if (!carousel) return;

    const container = carousel.closest('.product-bundle__products-container');

    const swiperConfig = {
      slidesPerView: 'auto',
      spaceBetween: 16,
      slidesOffsetBefore: 24,
      slidesOffsetAfter: 24,
      allowTouchMove: true,
      effect: 'slide',
      speed: 400,
      grabCursor: true,
      autoHeight: true,
      navigation: {
        prevEl: container?.querySelector('.swiper-button-prev'),
        nextEl: container?.querySelector('.swiper-button-next'),
      },
      breakpoints: {
        1560: {
          slidesOffsetBefore: 0,
          slidesOffsetAfter: 0,
          slidesPerView: 3,
        },
        1200: {
          slidesOffsetBefore: 0,
          slidesOffsetAfter: 0,
          slidesPerView: 2,
        },
        990: {
          slidesOffsetBefore: 0,
          slidesOffsetAfter: 0,
          slidesPerView: 4,
        },
      },
    };

    new Swiper(carousel, swiperConfig);
  }

  /**
   * @param {BundleProductToggleEvent} event
   */
  onBundleProductToggle(event) {
    const { selected, price, comparePrice, variantId } = event.detail;

    const currentCount = parseInt(this.refs.bundleCount.textContent || '0');
    this.refs.bundleCount.textContent = String(
      currentCount + (selected ? 1 : -1),
    );
    this.refs.btnBundleCount.textContent = String(
      currentCount + (selected ? 1 : -1),
    );

    let totalPrice = Number(this.refs.price.dataset.price);
    let totalComparePrice = Number(this.refs.comparePrice.dataset.comparePrice);

    if (selected) {
      totalPrice += Number(price);
      totalComparePrice += Number(comparePrice);
    } else {
      totalPrice -= Number(price);
      totalComparePrice -= Number(comparePrice);
    }

    this.refs.price.dataset.price = totalPrice.toString();
    this.refs.comparePrice.dataset.comparePrice = totalComparePrice.toString();
    this.refs.price.textContent = formatMoney(totalPrice);
    this.refs.comparePrice.textContent = formatMoney(totalComparePrice);
    this.refs.comparePrice.hidden = totalComparePrice === totalPrice;

    if (selected) {
      this.variantIds.push(variantId);
    } else {
      this.variantIds = this.variantIds.filter(
        (/** @type {string} */ id) => id !== variantId,
      );
    }
  }

  /**
   * @param {MouseEvent & {target: HTMLElement}} event - Click event.
   */
  async addBundleToCart(event) {
    const { btnAddBundleToCart } = this.refs;
    btnAddBundleToCart.disabled = true;
    btnAddBundleToCart.classList.add('atc-added');

    try {
      const cartResponse = await fetch(`${Theme.routes.cart_url}.js`);
      const cart = await cartResponse.json();
      const existingItems = cart.items || [];

      const itemsToAdd = [];
      const updatePromises = [];

      for (const id of this.variantIds) {
        const existingItem = existingItems.find(
          (
            /** @type {{variant_id: number, key: string, quantity: number, properties: Record<string, boolean>}} */ item,
          ) =>
            String(item.variant_id) === String(id) &&
            !item.properties._free_gift,
        );

        if (existingItem) {
          updatePromises.push(
            fetch(Theme.routes.cart_change_url, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({
                id: existingItem.key,
                quantity: existingItem.quantity + 1,
              }),
            }),
          );
        } else {
          itemsToAdd.push({ id, quantity: 1 });
        }
      }

      await Promise.all(updatePromises);

      if (itemsToAdd.length > 0) {
        await fetch(Theme.routes.cart_add_url, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ items: itemsToAdd }),
        });
      }

      this.dispatchEvent(
        new CartAddEvent({}, this.id, {
          source: 'product-form',
          itemCount: this.variantIds.length,
        }),
      );
    } catch (error) {
      console.error('Error:', error);
    } finally {
      btnAddBundleToCart.disabled = false;
      btnAddBundleToCart.classList.remove('atc-added');
    }
  }
}

if (!customElements.get('product-bundle-component')) {
  customElements.define('product-bundle-component', ProductBundleComponent);
}
